import { useSyncExternalStore } from 'react';
import { commitWrites, flushWrites, queueSnapshot, queueWrites, subscribeQueue } from '../vault/writeQueue';
import type { QueueView } from '../vault/writeQueue';
import type { FileWrite } from '../vault/sparseWriter';
import { getVaultState, reloadVault, subscribeVault } from './vaultStore';
import type { VaultState } from './vaultStore';

export type { VaultState };

/**
 * The tab's one vault, as a hook. Surfaces read this rather than knowing that a
 * vault has to be paired for, signed into, or refreshed.
 *
 * A reader, not an owner — the lifecycle lives in [vaultStore], so every screen
 * sees the same vault at the same version. What this adds is the subscription.
 */
export function useVault(): {
  state: VaultState;
  /** Re-read for every surface, not just this one. Re-reading without a reload
   *  is the only way to watch a revocation take effect: a reload drops the
   *  in-memory key and asks to pair again, which looks identical to being
   *  refused. */
  reload: () => void;
  /** Queued, not written — see writeQueue. Returns as soon as it is
   *  recorded, because the surface should not wait five seconds to feel
   *  responsive. */
  write: (writes: FileWrite[]) => void;
  /** Queued AND sent. For a discrete act, where there is no run of gestures
   *  to coalesce and the debounce only widens the window a reload can lose
   *  the change in. */
  commit: (writes: FileWrite[]) => Promise<void>;
  flush: () => Promise<void>;
  queue: QueueView;
} {
  const state = useSyncExternalStore(subscribeVault, getVaultState, getVaultState);
  const queue = useSyncExternalStore(subscribeQueue, queueSnapshot, queueSnapshot);

  return {
    state,
    reload: reloadVault,
    write: queueWrites,
    commit: commitWrites,
    flush: flushWrites,
    queue,
  };
}
